import type { SheetsUiCommand } from '@sharedlib/common/sheets/ui-commands';

/**
 * This tab as one of the user's Stitch clients, for the MCP contract's (6 Oct) `sheets_workbooks` / `sheets_ui`
 * "open": a workbook nobody has open has no editor to ask, so sheets-app asks the tab the person used last, and the
 * app shell (always loaded, outside the lazily loaded editor) opens it as "Open" in the workbook's menu does.
 *
 * - Presence: the tab reports itself (`{ activeAt }`) when it starts, every 20 s, when the person comes back to it
 *   (at most once a second) and when it moves to another space; it closes on `pagehide`. sheets-app forgets a tab
 *   60 s after its last report, and answers NOT_OPEN when the user has no tab and no editor.
 * - A click or a key in the tab sends nothing (Pavel, 2026-10-28: Mail clicks and To-field typing posted here): it
 *   moves `activeAt`, which the next report carries; so does focus coming back from the page's own frame (an
 *   email's body). Which tab was used last changes only when the person switches tab or window, and coming back
 *   reports at once.
 * - The claim: the server names one tab in the push (`editorSessionId` is that tab's id); every tab of the account
 *   hears it, only the named one acts, and only its ack counts. The id is made per page load and kept in memory
 *   only: a duplicated tab (which copies sessionStorage) is a second client, never a second claimant.
 *
 * Kept free of Angular and of the editor, so it is tested alone and costs the eager bundle a few hundred bytes.
 */

/** As AM-46's heartbeat (`APP_CLIENT_HEARTBEAT_MS` in sheets-app). */
export const APP_CLIENT_HEARTBEAT_MS = 20_000;
/** A person coming back to the tab is reported at most this often. */
export const APP_CLIENT_ACTIVITY_THROTTLE_MS = 1_000;
/**
 * How long the ack waits for the route: sheets-app waits 3 s in all (ALG-NAV step 6), and the lazy editor may take
 * longer than that to load. Past it, the open has started and is reported so (`status: "opening"`), never as done.
 */
export const APP_CLIENT_OPEN_BUDGET_MS = 2_000;

export interface AppClientAck {
  readonly commandId: string;
  readonly ok: boolean;
  readonly error: string | null;
  readonly state?: Record<string, unknown>;
}

/** A push as the signal carries it. */
export interface AppClientSignal {
  readonly editorSessionId: string;
  readonly workbookId: string;
  readonly commandId: string;
  readonly command: Record<string, unknown>;
}

export interface AppClientDeps {
  readonly clientId: string;
  now(): number;
  /** POST the report; best effort. */
  report(activeAt: number): void;
  /** The close, as a beacon when the page is going away. */
  close(beacon: boolean): void;
  ack(ack: AppClientAck): void;
  /** The app's own route to a workbook in the editor (`openWorkbookInEditor`); resolves to whether it went there. */
  openWorkbook(workbookId: string, sheetId?: string): Promise<boolean>;
  /** The address the tab shows, for the answer. */
  url(): string;
  setTimer(ms: number, fire: () => void): unknown;
  clearTimer(timer: unknown): void;
}

/** Why the tab didn't open it: the agent-facing reasons (never shown to a person). */
export const APP_CLIENT_REASONS = {
  onlyOpens: 'a Stitch tab with no editor open only opens workbooks (open_workbook); open the workbook first',
  sheetById: 'name the sheet by id (sheets_get_summary lists them) to open another workbook on it',
  refused: 'the open did not happen: the tab stayed where it was (the person chose to stay with unsaved changes, or the route refused it)',
  failed: 'the open failed in the tab',
} as const;

export class SheetsAppClient {
  private activeAt: number;
  private lastReport = -Infinity;
  private heartbeat: unknown = null;
  /** A use of the tab within a second of the last report goes out when the second is up. */
  private trailing: unknown = null;
  private running = false;

  public constructor(private readonly deps: AppClientDeps) {
    this.activeAt = deps.now();
  }

  public get id(): string {
    return this.deps.clientId;
  }

  /** Signed in: present from now on. */
  public start(focused: boolean): void {
    if (this.running) return;
    this.running = true;
    if (focused) this.activeAt = this.deps.now();
    this.send();
  }

  /** Signed out, or the page is going away: absent from now on. */
  public stop(beacon: boolean): void {
    if (!this.running) return;
    this.running = false;
    for (const timer of [this.heartbeat, this.trailing]) if (timer !== null) this.deps.clearTimer(timer);
    this.heartbeat = this.trailing = null;
    this.deps.close(beacon);
  }

  /** The person used the tab (a click, a key): when, for the next report. Sends nothing. */
  public used(): void {
    this.activeAt = this.deps.now();
  }

  /** The person came back to the tab: it is the one they used last. */
  public touched(): void {
    this.activeAt = this.deps.now();
    if (!this.running || this.trailing !== null) return;
    const wait = this.lastReport + APP_CLIENT_ACTIVITY_THROTTLE_MS - this.deps.now();
    if (wait <= 0) return this.send();
    this.trailing = this.deps.setTimer(wait, () => {
      this.trailing = null;
      if (this.running) this.send();
    });
  }

  /** The tab moved to another space: reported again, its activity as it was. */
  public moved(): void {
    if (this.running) this.send();
  }

  /** A push. Only one naming this tab is acted on; every tab hears every push. */
  public async handle(signal: AppClientSignal): Promise<void> {
    if (!this.running || signal.editorSessionId !== this.deps.clientId) return;
    const ui = (signal.command as { ui?: SheetsUiCommand }).ui;
    const fail = (error: string) => this.deps.ack({ commandId: signal.commandId, ok: false, error });
    if (!ui || ui.family !== 'ui' || ui.action !== 'open_workbook' || ui.workbookId !== signal.workbookId) return fail(APP_CLIENT_REASONS.onlyOpens);
    if (ui.sheet && !('id' in ui.sheet)) return fail(APP_CLIENT_REASONS.sheetById);
    const sheetId = ui.sheet && 'id' in ui.sheet ? ui.sheet.id : undefined;
    let outcome: boolean | 'pending' | Error;
    let timer: unknown = null;
    try {
      outcome = await Promise.race([
        this.deps.openWorkbook(ui.workbookId, sheetId),
        new Promise<'pending'>((resolve) => (timer = this.deps.setTimer(APP_CLIENT_OPEN_BUDGET_MS, () => resolve('pending')))),
      ]);
    } catch (error) {
      outcome = error instanceof Error ? error : new Error(APP_CLIENT_REASONS.failed);
    } finally {
      if (timer !== null) this.deps.clearTimer(timer);
    }
    if (outcome instanceof Error) return fail(`${APP_CLIENT_REASONS.failed}: ${outcome.message}`.slice(0, 500));
    if (outcome === false) return fail(APP_CLIENT_REASONS.refused);
    this.deps.ack({
      commandId: signal.commandId,
      ok: true,
      error: null,
      // "opening": the route has started and the editor is still loading; "opened": the tab is there.
      state: { workbookId: ui.workbookId, ...(sheetId ? { sheetId } : {}), status: outcome === 'pending' ? 'opening' : 'opened', route: this.deps.url() },
    });
  }

  private send(): void {
    this.lastReport = this.deps.now();
    this.deps.report(this.activeAt);
    if (this.heartbeat !== null) this.deps.clearTimer(this.heartbeat);
    this.heartbeat = this.deps.setTimer(APP_CLIENT_HEARTBEAT_MS, () => {
      this.heartbeat = null;
      if (this.running) this.send();
    });
  }
}
